// src/features/search/useProductSearch.js
//
// Global search hook — single source of truth for all search state.
//
// Features:
//   - 300ms debounced suggestions from /api/v1/search/suggestions
//   - In-memory suggestions cache (Map, 30s TTL) — avoids re-fetching same prefix
//   - AbortController per request — stale requests never overwrite fresh results
//   - Recent searches in localStorage (max 8 entries)
//   - Trending searches from /api/v1/search/trending (fetched once on open)
//   - URL sync on /shop — mirrors ?search= param into query input
//   - onSubmit: updates URL if on /shop, navigates to /shop?search= otherwise

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { searchAPI } from '@/api/search.api';

const DEBOUNCE_MS      = 300;
const MIN_QUERY_LEN    = 2;
const RECENT_KEY       = 'msga_recent_searches';
const RECENT_MAX       = 8;
const CACHE_TTL_MS     = 30_000; // 30 seconds

// ── In-memory suggestion cache ────────────────────────────────────────────────
// Map<string, { data: SuggestionsPayload, ts: number }>
const _cache = new Map();

function getCached(q) {
  const entry = _cache.get(q);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) { _cache.delete(q); return null; }
  return entry.data;
}

function setCache(q, data) {
  _cache.set(q, { data, ts: Date.now() });
  // Evict oldest when cache grows large
  if (_cache.size > 50) {
    const first = _cache.keys().next().value;
    _cache.delete(first);
  }
}

// ── Recent searches (localStorage) ───────────────────────────────────────────
function loadRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
}

function saveRecent(list) {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch {}
}

function addRecent(q) {
  const trimmed = q.trim();
  if (!trimmed) return;
  const prev = loadRecent().filter((s) => s !== trimmed);
  saveRecent([trimmed, ...prev].slice(0, RECENT_MAX));
}

function removeRecent(q) {
  saveRecent(loadRecent().filter((s) => s !== q));
}

function clearAllRecent() {
  saveRecent([]);
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useProductSearch() {
  const [query,       setQueryRaw]  = useState('');
  const [products,    setProducts]  = useState([]);
  const [categories,  setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [didYouMean,  setDidYouMean] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading,     setLoading]   = useState(false);
  const [error,       setError]     = useState(null);
  const [isOpen,      setIsOpen]    = useState(false);
  const [trending,    setTrending]  = useState([]);
  const [recent,      setRecent]    = useState(loadRecent);

  const navigate     = useNavigate();
  const location     = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const timerRef      = useRef(null);
  const controllerRef = useRef(null);
  const isShopPage    = location.pathname === '/shop';

  // ── URL → input sync when on /shop ─────────────────────────────────────────
  const urlSearch = searchParams.get('search') ?? '';
  useEffect(() => {
    if (isShopPage) setQueryRaw(urlSearch);
  }, [isShopPage, urlSearch]);

  // ── Fetch suggestions ───────────────────────────────────────────────────────
  const fetchSuggestions = useCallback(async (q) => {
    // Cache hit — instant render, no network
    const cached = getCached(q);
    if (cached) {
      setProducts(cached.products);
      setCategories(cached.categories);
      setSuggestions(cached.suggestions);
      setLoading(false);
      return;
    }

    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    setLoading(true);
    setError(null);

    try {
      const res = await searchAPI.suggestions(q, controllerRef.current.signal);
      const data = res.data ?? { products: [], categories: [], suggestions: [], did_you_mean: null, similar_products: [] };
      setProducts(data.products);
      setCategories(data.categories);
      setSuggestions(data.suggestions);
      setDidYouMean(data.did_you_mean ?? null);
      setSimilarProducts(data.similar_products ?? []);
      setCache(q, data);
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'AbortError') return;
      setError('Failed to load results');
      setProducts([]); setCategories([]); setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Debounced search effect ─────────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(timerRef.current);
    const trimmed = query.trim();

    if (!trimmed || trimmed.length < MIN_QUERY_LEN) {
      controllerRef.current?.abort();
      setProducts([]); setCategories([]); setSuggestions([]);
      setDidYouMean(null); setSimilarProducts([]);
      setLoading(false); setError(null);
      return;
    }

    timerRef.current = setTimeout(() => fetchSuggestions(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timerRef.current);
  }, [query, fetchSuggestions]);

  // ── Fetch trending on open (once) ───────────────────────────────────────────
  const trendingFetched = useRef(false);
  useEffect(() => {
    if (!isOpen || trendingFetched.current) return;
    trendingFetched.current = true;
    searchAPI.trending()
      .then((res) => setTrending(res.data?.trending ?? []))
      .catch(() => {}); // trending is non-critical
  }, [isOpen]);

  // ── Public API ──────────────────────────────────────────────────────────────
  const setQuery = useCallback((val) => setQueryRaw(val), []);

  const open = useCallback(() => {
    setIsOpen(true);
    setRecent(loadRecent());
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setLoading(false);
    controllerRef.current?.abort();
    clearTimeout(timerRef.current);
    if (!isShopPage) {
      setQueryRaw('');
      setProducts([]); setCategories([]); setSuggestions([]);
      setDidYouMean(null); setSimilarProducts([]);
      setError(null);
    }
  }, [isShopPage]);

  const clear = useCallback(() => {
    setQueryRaw('');
    setProducts([]); setCategories([]); setSuggestions([]);
    setDidYouMean(null); setSimilarProducts([]);
    setError(null);
  }, []);

  const retry = useCallback(() => {
    const t = query.trim();
    if (t.length >= MIN_QUERY_LEN) fetchSuggestions(t);
  }, [query, fetchSuggestions]);

  const onSubmit = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    addRecent(q);
    setRecent(loadRecent());
    setIsOpen(false);

    if (isShopPage) {
      const params = new URLSearchParams(searchParams);
      params.set('search', q);
      setSearchParams(params);
    } else {
      setQueryRaw('');
      setProducts([]); setCategories([]); setSuggestions([]);
      setDidYouMean(null); setSimilarProducts([]);
      navigate(`/shop?search=${encodeURIComponent(q)}`);
    }
  }, [query, isShopPage, searchParams, setSearchParams, navigate]);

  const onSelectSuggestion = useCallback((term) => {
    setQueryRaw(term);
    addRecent(term);
    setRecent(loadRecent());
    setIsOpen(false);

    if (isShopPage) {
      const params = new URLSearchParams(searchParams);
      params.set('search', term);
      setSearchParams(params);
    } else {
      navigate(`/shop?search=${encodeURIComponent(term)}`);
    }
  }, [isShopPage, searchParams, setSearchParams, navigate]);

  const deleteRecent = useCallback((term) => {
    removeRecent(term);
    setRecent(loadRecent());
  }, []);

  const clearRecent = useCallback(() => {
    clearAllRecent();          // writes [] to localStorage
    setRecent(loadRecent());   // reads back from localStorage — consistent with deleteRecent
  }, []);

  const hasQuery   = query.trim().length >= MIN_QUERY_LEN;
  const hasResults = products.length > 0 || categories.length > 0 || suggestions.length > 0;

  return {
    // Input
    query, setQuery,
    // Results
    products, categories, suggestions,
    didYouMean, similarProducts,
    loading, error,
    hasQuery, hasResults,
    // UI state
    isOpen, open, close, clear, retry,
    // Actions
    onSubmit, onSelectSuggestion,
    // Recent searches
    recent, deleteRecent, clearRecent,
    // Trending
    trending,
  };
}
